import { z } from 'zod';
import { IsoDateTimeSchema, LocaleSchema, TimezoneSchema, UuidSchema } from './common';
import { REVIEW_CHANNEL_PREFERENCES } from './reviews';
import {
  BUSINESS_CATEGORIES,
  LegalAcceptanceSchema,
  MEMBERSHIP_ROLES,
  MEMBERSHIP_STATUSES,
} from './tenants';
import { isLikelyGoogleReviewUrl, SmsSenderNameSchema } from './onboarding';

// ─── business profile ───────────────────────────────────────────────────────

// Timezone validation (`TimezoneSchema`, `isValidTimezone`) lives in ./common so onboarding can
// share it without an import cycle (this file imports ./onboarding).

export const CountrySchema = z
  .string()
  .regex(/^[A-Za-z]{2}$/)
  .transform((v) => v.toUpperCase());

export const PrivacyUrlSchema = z
  .union([
    z.literal('').transform(() => null),
    z
      .string()
      .trim()
      .max(500)
      .regex(/^https:\/\/[^\s<>"']+$/i, { message: 'invalid_url' }),
  ])
  .nullable();

export const UpdateTenantProfileSchema = z
  .object({
    name: z.string().trim().min(2).max(120).optional(),
    locale: LocaleSchema.optional(),
    timezone: TimezoneSchema.optional(),
    country: CountrySchema.optional(),
    businessCategory: z.enum(BUSINESS_CATEGORIES).nullable().optional(),
    /**
     * The business's own privacy policy (https). Public form pages link to it; without it they
     * show a notice generated from the business details (FORMS_DESIGN §6.3). '' clears it.
     */
    privacyUrl: PrivacyUrlSchema.optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'nothing_to_update' });
export type UpdateTenantProfileRequest = z.input<typeof UpdateTenantProfileSchema>;

export const LOGO_CONTENT_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;
/**
 * One logo per business, used in the app and on the public review page (inlined there as a data
 * URL), so it must fit REVIEW_LOGO_MAX_CHARS once base64-encoded: 140 KB → ~191k characters.
 */
export const LOGO_MAX_BYTES = 140 * 1024;

export const TenantProfileSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  slug: z.string(),
  businessCategory: z.string().nullable(),
  locale: LocaleSchema,
  timezone: z.string(),
  country: z.string(),
  logo: z
    .object({
      fileId: UuidSchema,
      contentType: z.string(),
      sizeBytes: z.number().int(),
      /** data: URL (logos are small; avoids an authenticated binary fetch for <img>). */
      dataUrl: z.string(),
    })
    .nullable(),
  privacyUrl: z.string().nullable().optional(),
});
export type TenantProfile = z.infer<typeof TenantProfileSchema>;

// ─── notifications (per user, per business) ─────────────────────────────────

export const NotificationPrefsSchema = z.object({
  negativeFeedback: z.boolean().default(true),
  failedDeliveries: z.boolean().default(true),
  lowCredits: z.boolean().default(true),
  weeklyDigest: z.boolean().default(true),
  teamChanges: z.boolean().default(false),
});
export type NotificationPrefs = z.infer<typeof NotificationPrefsSchema>;
export const UpdateNotificationPrefsSchema = NotificationPrefsSchema.partial().refine(
  (v) => Object.keys(v).length > 0,
  { message: 'nothing_to_update' },
);

// ─── store (default location) ───────────────────────────────────────────────

export const StoreSettingsSchema = z.object({
  locationId: UuidSchema,
  name: z.string(),
  address: z.string().nullable(),
  phone: z.string().nullable(),
  googleReviewUrl: z.string().nullable(),
  senderName: z.string().nullable(),
  channel: z.enum(REVIEW_CHANNEL_PREFERENCES),
  timezone: z.string(),
});
export type StoreSettings = z.infer<typeof StoreSettingsSchema>;

const nullableText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

export const UpdateStoreSettingsSchema = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    address: nullableText(300),
    phone: nullableText(32),
    googleReviewUrl: z
      .union([
        z.literal('').transform(() => null),
        z.url().refine(isLikelyGoogleReviewUrl, { message: 'not_a_google_review_link' }),
      ])
      .nullable()
      .optional(),
    senderName: SmsSenderNameSchema.nullable().optional(),
    channel: z.enum(REVIEW_CHANNEL_PREFERENCES).optional(),
    timezone: TimezoneSchema.optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'nothing_to_update' });
export type UpdateStoreSettingsRequest = z.input<typeof UpdateStoreSettingsSchema>;

// ─── team & invitations ─────────────────────────────────────────────────────

export const INVITATION_TTL_DAYS = 7;
export const INVITABLE_ROLES = ['admin', 'member'] as const;

export const TeamMemberSchema = z.object({
  userId: z.string(),
  email: z.string().nullable(),
  displayName: z.string().nullable(),
  role: z.enum(MEMBERSHIP_ROLES),
  status: z.enum(MEMBERSHIP_STATUSES),
  joinedAt: IsoDateTimeSchema,
  isYou: z.boolean(),
});
export type TeamMember = z.infer<typeof TeamMemberSchema>;

export const INVITATION_STATUSES = ['pending', 'expired', 'accepted', 'revoked'] as const;
export type InvitationStatus = (typeof INVITATION_STATUSES)[number];

export const TeamInvitationSchema = z.object({
  id: UuidSchema,
  email: z.string(),
  role: z.enum(MEMBERSHIP_ROLES),
  status: z.enum(INVITATION_STATUSES),
  invitedBy: z.string(),
  invitedByName: z.string().nullable(),
  expiresAt: IsoDateTimeSchema,
  createdAt: IsoDateTimeSchema,
});
export type TeamInvitation = z.infer<typeof TeamInvitationSchema>;

export const TeamResponseSchema = z.object({
  members: z.array(TeamMemberSchema),
  invitations: z.array(TeamInvitationSchema),
  seats: z.object({ used: z.number().int(), limit: z.number().int().nullable() }),
  yourRole: z.enum(MEMBERSHIP_ROLES),
  canManage: z.boolean(),
});
export type TeamResponse = z.infer<typeof TeamResponseSchema>;

export const CreateInvitationRequestSchema = z.object({
  email: z.email().max(254),
  role: z.enum(INVITABLE_ROLES),
});
export type CreateInvitationRequest = z.input<typeof CreateInvitationRequestSchema>;

export const CreateInvitationResponseSchema = z.object({
  invitation: TeamInvitationSchema,
  /** The invitation email was accepted by the email provider. */
  emailSent: z.boolean(),
  /** Only returned outside production (dev/test convenience); in production the link goes by email. */
  acceptUrl: z.string().optional(),
});
export type CreateInvitationResponse = z.infer<typeof CreateInvitationResponseSchema>;

export const UpdateMemberRoleRequestSchema = z.object({ role: z.enum(MEMBERSHIP_ROLES) });

/** Invitation preview for the accept page (token holder only). */
export const InvitationPreviewSchema = z.object({
  tenantName: z.string(),
  role: z.enum(MEMBERSHIP_ROLES),
  status: z.enum(INVITATION_STATUSES),
  /** Masked address the invitation was sent to. */
  emailHint: z.string(),
  /** Signed-in user's email matches the invitation. */
  emailMatches: z.boolean(),
  /** Signed-in user's email is verified (accepting requires it: 403 `email_unverified`). */
  emailVerified: z.boolean(),
  alreadyMember: z.boolean(),
  expiresAt: IsoDateTimeSchema,
  /** End of the free trial, when the business is on one (owner invitations from Super Admin). */
  trialEndsAt: IsoDateTimeSchema.nullable().default(null),
});
export type InvitationPreview = z.infer<typeof InvitationPreviewSchema>;

export const MyInvitationSchema = z.object({
  id: UuidSchema,
  tenantId: UuidSchema,
  tenantName: z.string(),
  role: z.enum(MEMBERSHIP_ROLES),
  expiresAt: IsoDateTimeSchema,
});
export const MyInvitationsResponseSchema = z.object({
  invitations: z.array(MyInvitationSchema),
  /** False when the signed-in email is not verified (invitations by email are then hidden). */
  emailVerified: z.boolean(),
});
export type MyInvitationsResponse = z.infer<typeof MyInvitationsResponseSchema>;

/** Required when the invitation makes the caller the owner: the terms are accepted for the business. */
const AcceptLegalField = { acceptLegal: LegalAcceptanceSchema.optional() };

export const AcceptInvitationRequestSchema = z.union([
  z.object({ token: z.string().regex(/^[A-Za-z0-9_-]{43}$/), ...AcceptLegalField }),
  z.object({ invitationId: UuidSchema, ...AcceptLegalField }),
]);
export type AcceptInvitationRequest = z.input<typeof AcceptInvitationRequestSchema>;

export const AcceptInvitationResponseSchema = z.object({
  tenantId: UuidSchema,
  tenantName: z.string(),
  role: z.enum(MEMBERSHIP_ROLES),
});
export type AcceptInvitationResponse = z.infer<typeof AcceptInvitationResponseSchema>;

// ─── data export & account closure ──────────────────────────────────────────

export const TENANT_REQUEST_KINDS = ['data_export', 'account_closure'] as const;
export type TenantRequestKind = (typeof TENANT_REQUEST_KINDS)[number];
export const TENANT_REQUEST_STATUSES = [
  'pending',
  'processing',
  'completed',
  'failed',
  'cancelled',
] as const;
export type TenantRequestStatus = (typeof TENANT_REQUEST_STATUSES)[number];

export const EXPORT_DOWNLOAD_TTL_DAYS = 7;

export const DataExportSchema = z.object({
  id: UuidSchema,
  status: z.enum(TENANT_REQUEST_STATUSES),
  requestedBy: z.string(),
  createdAt: IsoDateTimeSchema,
  completedAt: IsoDateTimeSchema.nullable(),
  expiresAt: IsoDateTimeSchema.nullable(),
  /** Available for download (completed, file present, not expired). */
  downloadable: z.boolean(),
  sizeBytes: z.number().int().nullable(),
  counts: z.record(z.string(), z.number().int()).nullable(),
  error: z.string().nullable(),
});
export type DataExport = z.infer<typeof DataExportSchema>;
export const DataExportsResponseSchema = z.object({ exports: z.array(DataExportSchema) });

export const AccountClosureSchema = z.object({
  id: UuidSchema,
  status: z.enum(TENANT_REQUEST_STATUSES),
  reason: z.string().nullable(),
  requestedBy: z.string(),
  createdAt: IsoDateTimeSchema,
  cancelledAt: IsoDateTimeSchema.nullable(),
});
export type AccountClosure = z.infer<typeof AccountClosureSchema>;
export const AccountClosureResponseSchema = z.object({ request: AccountClosureSchema.nullable() });

export const AccountClosureRequestSchema = z.object({
  /** Must equal the business name (deliberate confirmation). */
  confirmName: z.string().trim().min(1).max(120),
  reason: z.string().trim().max(1000).optional(),
});

// ─── messaging defaults (kept for the messaging module) ─────────────────────

export const MessagingDefaultsSchema = z.object({
  smsSenderName: z
    .string()
    .regex(/^[A-Za-z0-9 ]{1,11}$/, 'max 11 latin letters/digits')
    .optional(),
  emailFromName: z.string().max(80).optional(),
  replyToEmail: z.email().optional(),
  smsOptOutText: z.object({ el: z.string(), en: z.string() }).partial().optional(),
  /**
   * No default here on purpose: an unset tenant window falls through to the platform default
   * (`platform_config` `messaging.defaultQuietHours`), then to 21:00–09:00 (messaging/context.ts).
   */
  quietHours: z.object({ start: z.string(), end: z.string() }).optional(),
});
export type MessagingDefaults = z.infer<typeof MessagingDefaultsSchema>;

const HHMM = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'HH:MM');
const OPT_OUT_TEXT = z
  .string()
  .trim()
  .max(80)
  .refine((v) => v === '' || /\{\{\s*unsubscribeLink\s*\}\}/.test(v), {
    message: 'must contain {{unsubscribeLink}}',
  });

/**
 * GET/PUT /settings/messaging — what marketing messages carry and when they may go out.
 * The SMS opt-out line is appended to every campaign/automation SMS; when no custom line is set
 * the default one (`defaultSmsOptOutLine`) is used, so an SMS never goes out without an opt-out.
 * A custom line must contain `{{unsubscribeLink}}` (there is no STOP-reply keyword handling).
 */
export const MessagingSettingsSchema = z.object({
  smsOptOutText: z.object({ el: z.string().nullable(), en: z.string().nullable() }),
  /** The opt-out line used when no custom one is set, per locale (with a sample link). */
  defaultSmsOptOutText: z.object({ el: z.string(), en: z.string() }),
  /** The tenant's own window, or null when the platform default applies. */
  quietHours: z.object({ start: z.string(), end: z.string() }).nullable(),
  /** Window in effect (tenant → platform default). */
  effectiveQuietHours: z.object({ start: z.string(), end: z.string() }),
});
export type MessagingSettings = z.infer<typeof MessagingSettingsSchema>;

export const UpdateMessagingSettingsSchema = z
  .object({
    smsOptOutText: z
      .object({ el: OPT_OUT_TEXT.nullable(), en: OPT_OUT_TEXT.nullable() })
      .partial()
      .optional(),
    quietHours: z
      .object({ start: HHMM, end: HHMM })
      .refine((w) => w.start !== w.end, { message: 'start and end must differ' })
      .nullable()
      .optional(),
  })
  .strict();
export type UpdateMessagingSettings = z.infer<typeof UpdateMessagingSettingsSchema>;
